/**
 * Token storage and silent refresh.
 *
 * The whole OAuth flow runs in the browser against Cognito (public client,
 * PKCE), so the refresh token lives in localStorage alongside the access
 * token. That is the deliberate trade-off for having no auth backend; see the
 * README for the reasoning and when to revisit it.
 */

import { oidc } from '../config';

const ACCESS_TOKEN_KEY = 'hawk_plugins_access_token';
const REFRESH_TOKEN_KEY = 'hawk_plugins_refresh_token';

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  token_type: string;
  expires_in: number;
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch (err) {
    console.error('localStorage unavailable:', err);
  }
}

export function getStoredAccessToken(): string | null {
  return read(ACCESS_TOKEN_KEY);
}

export function storeTokens(tokens: TokenResponse): void {
  write(ACCESS_TOKEN_KEY, tokens.access_token);
  // Cognito omits refresh_token from refresh responses; keep the existing one.
  if (tokens.refresh_token) write(REFRESH_TOKEN_KEY, tokens.refresh_token);
}

export function clearTokens(): void {
  write(ACCESS_TOKEN_KEY, null);
  write(REFRESH_TOKEN_KEY, null);
}

export function getRefreshToken(): string | null {
  return read(REFRESH_TOKEN_KEY);
}

/** Decode a JWT payload without verifying it (the proxy verifies). */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function isExpired(token: string): boolean {
  const claims = decodeJwtPayload(token);
  const exp = claims?.exp;
  if (typeof exp !== 'number') return true;
  // 30 s buffer for clock skew and in-flight requests.
  return exp < Math.floor(Date.now() / 1000) + 30;
}

/** Email (or username) of the logged-in user, for display only. */
export function getCurrentUserLabel(): string | null {
  const token = getStoredAccessToken();
  if (!token) return null;
  const claims = decodeJwtPayload(token);
  const candidate = claims?.email ?? claims?.username ?? claims?.sub;
  return typeof candidate === 'string' ? candidate : null;
}

async function postToken(body: Record<string, string>): Promise<TokenResponse> {
  const response = await fetch(oidc.tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body).toString(),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Token endpoint returned ${response.status}: ${text}`);
  }
  return (await response.json()) as TokenResponse;
}

/** Exchange an authorization code (PKCE) for tokens and store them. */
export async function exchangeCode(
  code: string,
  codeVerifier: string,
  redirectUri: string
): Promise<void> {
  const tokens = await postToken({
    grant_type: 'authorization_code',
    client_id: oidc.clientId,
    code,
    code_verifier: codeVerifier,
    redirect_uri: redirectUri,
  });
  storeTokens(tokens);
}

// Singleton so concurrent callers share one refresh request.
let refreshPromise: Promise<string | null> | null = null;

async function refresh(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  try {
    const tokens = await postToken({
      grant_type: 'refresh_token',
      client_id: oidc.clientId,
      refresh_token: refreshToken,
    });
    storeTokens(tokens);
    return tokens.access_token;
  } catch (err) {
    console.warn('Token refresh failed; clearing session', err);
    clearTokens();
    return null;
  }
}

/**
 * A current access token, refreshing silently if the stored one is expired.
 * Returns null when the user has to log in again.
 */
export async function getValidToken(): Promise<string | null> {
  const token = getStoredAccessToken();
  if (token && !isExpired(token)) return token;
  if (!refreshPromise) {
    refreshPromise = refresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/** Best-effort revocation of the refresh token (Cognito supports this for public clients). */
export async function revokeRefreshToken(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (!refreshToken || !oidc.revocationEndpoint) return;
  try {
    await fetch(oidc.revocationEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        token: refreshToken,
        client_id: oidc.clientId,
      }).toString(),
    });
  } catch (err) {
    console.warn('Token revocation failed', err);
  }
}
